/**
 * Interceptor del modo vista previa: cuando esta activo (solo en localhost),
 * responde las llamadas a la API con los datos sembrados en preview-data.ts
 * en vez de salir a la red.
 *
 * Va como interceptor de clase (HTTP_INTERCEPTORS) porque la app ya usa
 * withInterceptorsFromDi() para el interceptor de MSAL, y mezclarlo con los
 * funcionales rompe la cadena de inyeccion.
 *
 * Fuera de localhost devuelve la peticion sin tocar, asi que en cualquier
 * despliegue este interceptor es transparente.
 */
import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { delay } from 'rxjs/operators';

import { enModoPreview } from './preview-mode';
import {
  PROYECTOS_DEMO,
  RESPUESTA_PROYECTOS,
  catalogoDemo,
  detalleProyectoDemo,
  respuestaMonitor
} from './preview-data';
import { respuestaCollab } from './preview-collab';
import { respuestaRoles } from './preview-roles';
import { respuestaCertificaciones } from './preview-certificaciones';
import { respuestaEdt } from './preview-edt';
import { respuestaDocumentos } from './preview-documentos';
import { respuestaRiesgos } from './preview-riesgos';
import { respuestaVentas } from './preview-ventas';
import { respuestaCierre } from './preview-cierre';
import { respuestaSalud } from './preview-salud';
import { respuestaEquipo } from './preview-equipo';

/**
 * La pantalla pide el proyecto por id —el código se repite entre sociedades—
 * y los datos sembrados están armados por código. Se traduce acá, una sola
 * vez, en vez de en cada archivo de la vista previa.
 */
function porCodigo(ruta: string): string {
  return ruta.replace(/\/api\/project\/(\d+)(?=\/|$)/, (entera, id: string) => {
    const proyecto = PROYECTOS_DEMO.find((p) => p.id === Number(id));
    return proyecto ? `/api/project/${encodeURIComponent(proyecto.code)}` : entera;
  });
}

function respuestaSembrada(metodo: string, url: string, cuerpo: unknown): unknown | null {
  const [conId, query] = url.split('?');
  const ruta = porCodigo(conId);

  /* Las preferencias de la persona (columnas del tablero). En la vista previa
     se guardan en el navegador, para poder probar que sobreviven a recargar. */
  const preferencia = ruta.match(/\/api\/preferencias\/([^/]+)$/);
  if (preferencia) {
    const clave = `vista-previa.preferencia.${preferencia[1]}`;
    try {
      if (metodo === 'PUT') {
        localStorage.setItem(clave, JSON.stringify((cuerpo as { valor?: unknown })?.valor ?? null));
        return { success: true };
      }
      return { success: true, valor: JSON.parse(localStorage.getItem(clave) ?? 'null') };
    } catch {
      return { success: true, valor: null };
    }
  }
  const params = new URLSearchParams(query ?? '');

  /* El chatter va primero porque necesita el metodo: /comments responde
     distinto a GET que a POST, y el resto de la vista previa solo lee. */
  const collab = respuestaCollab(metodo, ruta, params, cuerpo);
  if (collab !== null) {
    return collab;
  }

  /* El equipo tambien necesita el metodo: la misma ruta lista a GET y guarda
     a POST, y el aviso a Trakkinn es otro POST distinto. */
  const equipo = respuestaEquipo(metodo, ruta, params, cuerpo);
  if (equipo !== null) {
    return equipo;
  }

  const salud = respuestaSalud(ruta, params);
  if (salud !== null) {
    return salud;
  }

  const cierre = respuestaCierre(metodo, ruta, cuerpo);
  if (cierre !== null) {
    return cierre;
  }

  const roles = respuestaRoles(metodo, ruta, params, cuerpo);
  if (roles !== null) {
    return roles;
  }

  /* Tambien antes que las rutas de proyecto: /project/X/certificaciones
     empieza igual que /project/X y sin esto lo atenderia el detalle. */
  const certificaciones = respuestaCertificaciones(metodo, ruta, params, cuerpo);
  if (certificaciones !== null) {
    return certificaciones;
  }

  const documentos = respuestaDocumentos(metodo, ruta, params, cuerpo);
  if (documentos !== null) {
    return documentos;
  }

  const riesgos = respuestaRiesgos(metodo, ruta, cuerpo);
  if (riesgos !== null) {
    return riesgos;
  }

  const edt = respuestaEdt(metodo, ruta, cuerpo, params);
  if (edt !== null) {
    return edt;
  }

  /* Tambien antes del detalle del proyecto, por lo mismo que las
     certificaciones: /project/X/facturas empieza igual que /project/X. */
  const ventas = respuestaVentas(metodo, ruta, params);
  if (ventas !== null) {
    return ventas;
  }

  if (/\/api\/projects\/?$/.test(ruta)) {
    return RESPUESTA_PROYECTOS;
  }

  // El detalle de costos de ARMUS (3/11/2026)
  if (/\/api\/project\/[^/]+\/costos\/movimientos$/.test(ruta)) {
    const fila = (id: number, fecha: string, documento: string, descripcion: string, elemento: string, componente: string, importe: number) => ({
      id, fecha, documento, origen: 'Factura de compra', proveedor: 'Proveedor de ejemplo', descripcion,
      elemento, componente, importe_original: importe, moneda_original: 'USD', importe
    });
    return {
      success: true, moneda: params.get('moneda') ?? 'USD', sin_cotizacion: false,
      data: [
        fila(3, '2026-10-02', 'FA-A 00001-00000031', 'GTO-0005 Seguros', '3 Regularización', 'Sin componente de costo', 30.91),
        fila(2, '2026-07-03', 'FA-A 00001-00000022', 'P00262: Impuestos', '2.2 Gestión', 'GTO-0003 Honorarios contables', 120),
        fila(1, '2026-03-31', 'FA-A 00001-00000011', 'P00232: Movilidad y traslados', '3 Regularización', 'Sin componente de costo', 21.59)
      ],
      total_contabilizado: 172.5
    };
  }

  /* Los paneles del Resumen (2/11/2026): catorce meses, para que se puedan
     probar el trimestre y el año. */
  if (/\/api\/project\/[^/]+\/series$/.test(ruta)) {
    const meses = ['2025-10', '2025-11', '2025-12', '2026-01', '2026-02', '2026-03', '2026-04',
      '2026-05', '2026-06', '2026-07', '2026-08', '2026-09', '2026-10', '2026-11'];
    return {
      success: true,
      moneda: params.get('moneda') ?? 'USD',
      corte: null,
      sin_cotizacion: false,
      fuente: 'imputacion',
      presupuesto_gasto: 120000,
      data: meses.map((mes, i) => ({
        mes,
        gasto: i === 5 ? 0 : 5200 + ((i * 1370) % 4100),
        ventas: i % 3 === 2 ? 0 : 9000 + ((i * 2210) % 5000),
        ingresos: i < 2 ? 0 : 7600 + ((i * 1830) % 6200)
      }))
    };
  }

  const detalle = ruta.match(/\/api\/project\/([^/]+)\/?$/);
  if (detalle) {
    return detalleProyectoDemo(decodeURIComponent(detalle[1]), params.get('moneda'));
  }

  /* Los catalogos del sistema. El detalle los pide todos juntos con un
     forkJoin, asi que si uno solo se va a la red la pantalla entera queda en
     blanco. */
  const catalogo = catalogoDemo(ruta);
  if (catalogo !== null) {
    return catalogo;
  }

  // El monitor tiene varias rutas: las resuelve su propio archivo
  const monitor = respuestaMonitor(ruta, params);
  if (monitor !== null) {
    return monitor;
  }

  return null;
}

@Injectable()
export class PreviewInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!enModoPreview()) return next.handle(req);

    /* urlWithParams y no url: en Angular los parametros de consulta viven en
       req.params, y req.url viene sin ellos. Con la url pelada, los filtros
       del monitor nunca llegaban a los datos sembrados. */
    const body = respuestaSembrada(req.method, req.urlWithParams, req.body);
    if (body === null) return next.handle(req);

    /* Una respuesta sembrada que dice `success: false` **tiene que fallar**.
       Devolviendo todo con 200, un rechazo del backend —«falta el motivo del
       cambio de presupuesto»— llegaba a la pantalla como si hubiera salido
       bien: el camino de error no se podía probar en vista previa, que es
       justo donde se prueba. */
    const rechazado = !!body
      && typeof body === 'object'
      && (body as { success?: boolean }).success === false;

    if (rechazado) {
      return throwError(() => new HttpErrorResponse({
        status: 422,
        statusText: 'Unprocessable Content',
        url: req.urlWithParams,
        error: body
      })).pipe(delay(150));
    }

    // El delay imita la latencia real: sirve para ver los estados de carga.
    return of(new HttpResponse({ status: 200, body })).pipe(delay(150));
  }
}
